import type { ReactNode } from "react";

interface RepeatableListProps<T> {
  label: string;
  items: T[];
  onChange: (items: T[]) => void;
  createItem: () => T;
  renderItem: (item: T, onItemChange: (item: T) => void) => ReactNode;
}

/** Generic add/remove/reorder editor for an array of cards (steps, stats,
 * feature cards, etc.) — every repeatable block on the marketing site
 * (see Home/Company/Solutions editors) is built on this one primitive
 * instead of hand-rolling add/remove UI per section. */
export function RepeatableList<T>({ label, items, onChange, createItem, renderItem }: RepeatableListProps<T>) {
  function updateAt(index: number, item: T) {
    onChange(items.map((existing, i) => (i === index ? item : existing)));
  }

  function removeAt(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function moveBy(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  return (
    <div className="repeatable-list">
      <span className="field-label">{label}</span>
      {items.map((item, index) => (
        <div className="repeatable-item" key={index}>
          <div className="repeatable-item-body">{renderItem(item, (updated) => updateAt(index, updated))}</div>
          <div className="repeatable-item-controls">
            <button type="button" onClick={() => moveBy(index, -1)} disabled={index === 0} title="Move up">
              ↑
            </button>
            <button
              type="button"
              onClick={() => moveBy(index, 1)}
              disabled={index === items.length - 1}
              title="Move down"
            >
              ↓
            </button>
            <button type="button" onClick={() => removeAt(index)} title="Remove" className="danger">
              Remove
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, createItem()])}>
        + Add
      </button>
    </div>
  );
}
